import { motion } from 'framer-motion';
import { cx } from './cx';

export type ProgressBarTone = 'orange' | 'gold' | 'teal';
export type ProgressBarSize = 'md' | 'thin';

export interface ProgressBarProps {
  /** Percent filled, 0 to 100 (clamped; anything not a number is 0). */
  value: number;
  /** orange (default) for progress, gold for mastered, teal for a score. */
  tone?: ProgressBarTone;
  /** md 5px (default), thin 4px. */
  size?: ProgressBarSize;
  /**
   * Set when no nearby text states the value: the bar becomes a named
   * progressbar. Without it the bar is decorative (the percentage is
   * already written beside it everywhere the app draws one today).
   */
  label?: string;
  /** Grow the fill from 0 on mount (the Course unit card). */
  animate?: boolean;
  className?: string;
}

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/**
 * A slim rounded progress track with one fill (redesign PR6, promoted from
 * the Course page). Spans only, so it can sit inside a button.
 */
export function ProgressBar({
  value,
  tone = 'orange',
  size = 'md',
  label,
  animate = false,
  className,
}: ProgressBarProps) {
  const pct = clamp(value);
  const fillClass = cx(
    'ee-progress-bar__fill',
    tone !== 'orange' && `ee-progress-bar__fill--${tone}`,
  );
  const a11y = label
    ? {
        role: 'progressbar',
        'aria-label': label,
        'aria-valuenow': Math.round(pct),
        'aria-valuemin': 0,
        'aria-valuemax': 100,
      }
    : {};
  return (
    <span
      {...a11y}
      className={cx('ee-progress-bar', size === 'thin' && 'ee-progress-bar--thin', className)}
    >
      {animate ? (
        <motion.span
          className={fillClass}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      ) : (
        <span className={fillClass} style={{ width: `${pct}%` }} />
      )}
    </span>
  );
}
